import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/shop';
import { QrCode } from '../components/QrCode';
import { brand, formatMoney } from '../brand/brand';
import type { VoucherPass as Pass } from '../api/types';

/**
 * A voucher's QR code, on whichever phone is holding the link.
 *
 * ── This page is deliberately anonymous, and deliberately almost empty ───────
 *
 * The person looking at it may not be the person who bought the voucher, may not
 * have the app, and may not have an account. That is the point: the customer sends
 * themselves a link so the code is on the phone in their hand at the counter.
 *
 * WHAT IS NOT ON THIS PAGE:
 *
 *   THE PIN. Never. The counter demands it on every lookup, so a forwarded link is
 *   half a credential — whoever holds it can make the till find the voucher and
 *   then be asked for four digits they do not have. Putting the PIN here to save
 *   the customer reading it out would convert this into a way to spend somebody
 *   else's money by forwarding a message.
 *
 *   THE CUSTOMER'S NAME OR CONTACT DETAILS. A link sent to a wrong number should
 *   be an inconvenience, not a privacy incident.
 *
 *   ANY NAVIGATION INTO THE ACCOUNT. There is no header, no menu and no sign-in
 *   prompt. The page shows one voucher and offers nothing else.
 */

export function VoucherPass() {
  const { token } = useParams<{ token: string }>();

  const [pass, setPass] = useState<Pass | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (token === undefined) {
      setFailed(true);
      return;
    }

    api.voucherPass(token).then(setPass).catch(() => setFailed(true));
  }, [token]);

  if (failed) {
    return (
      <div className="pass">
        <div className="pass-card">
          <h1>This link no longer works</h1>
          {/* One message for expired, revoked and never-existed alike — the API
              answers the same way for all three, and so does this. */}
          <p className="muted">
            It may have expired, or the person who sent it may have cancelled it.
            Ask them to send a new one from their {brand.name} account.
          </p>
        </div>
      </div>
    );
  }

  if (pass === null) {
    return (
      <div className="pass">
        <div className="pass-card"><p className="muted">Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="pass">
      <div className="pass-card">
        <span className="pass-business">{pass.businessName}</span>
        <h1>{pass.dealTitle}</h1>
        {pass.branchName !== null && (
          <p className="muted">Only at {pass.branchName}</p>
        )}

        {pass.spendable ? (
          <>
            {/* THE WHOLE POINT OF THE PAGE. Large, centred, on white, with nothing
                competing for the screen — a cashier's scanner has to read this off
                a phone held at arm's length in whatever lighting the shop has. */}
            <div className="pass-qr">
              <QrCode value={pass.code} size={260} />
            </div>

            <p className="pass-code" aria-label={pass.code.split('').join(' ')}>
              {pass.code}
            </p>

            {/* formatMoney uses the brand's own currency, which is the platform's.
                The pass carries the business's currencyCode for the day a partner
                trades in another one — until formatMoney takes an override, using
                it here would print the right symbol against the wrong locale. */}
            <p className="pass-balance">{formatMoney(pass.balanceCents)} left</p>

            {pass.pinRequired && (
              // Said HERE rather than at the till. Somebody holding a forwarded
              // link needs to know they will be asked for four digits before they
              // are at the counter with a queue behind them.
              <p className="pass-note">
                The counter will also ask for the 4-digit PIN. It is not on this
                page — whoever bought this voucher has it in their account.
              </p>
            )}
          </>
        ) : (
          <div className="pass-dead">
            <h2>This voucher cannot be used</h2>
            <p className="muted">
              {pass.balanceCents <= 0
                ? 'There is nothing left on it.'
                : 'It passed its expiry date.'}
            </p>
            {/* Shown rather than a QR that would be refused at the counter. Better
                to find out here than in front of a queue. */}
          </div>
        )}

        <p className="pass-expiry">
          {pass.spendable ? 'Valid until ' : 'Expired '}
          {new Date(pass.expiresAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
